import { authStorage } from '@/lib/auth/auth-storage';
import { getJwtExpiration } from '@/lib/auth/auth-utils';
import type {
  ApiErrorResponse,
  AuthSession,
  BusinessSessionData,
  RefreshSessionResponse,
  RequestOtpResponse,
  VerifyOtpRequest,
  VerifyOtpResponse,
} from '@/lib/auth/types';

const DEFAULT_REQUEST_OTP_MESSAGE = 'Si el numero esta habilitado, te enviaremos un codigo.';
const DEFAULT_VERIFY_ERROR_MESSAGE = 'Codigo invalido o expirado. Intenta de nuevo.';
const DEFAULT_NETWORK_ERROR_MESSAGE = 'No pudimos conectar con el servidor. Intenta nuevamente.';
const FALLBACK_SESSION_SECONDS = 8 * 60 * 60;
const REFRESH_BEFORE_EXPIRY_MS = 2 * 60 * 1000;

let activeRefreshPromise: Promise<AuthSession | null> | null = null;

class AuthServiceError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'AuthServiceError';
    this.status = status;
  }
}

function getApiBaseUrl(): string {
  const apiBaseUrl = process.env.NEXT_PUBLIC_API_BASE_URL?.trim();

  if (!apiBaseUrl) {
    throw new AuthServiceError('Configura NEXT_PUBLIC_API_BASE_URL para usar el login OTP.');
  }

  return apiBaseUrl.replace(/\/+$/, '');
}

function buildApiUrl(path: string): string {
  const apiBaseUrl = getApiBaseUrl();
  return `${apiBaseUrl}${path}`;
}

function isJwtLike(token: string): boolean {
  const parts = token.split('.');
  return parts.length === 3 && parts[0].startsWith('eyJ') && parts.every((part) => part.length > 0);
}

function buildHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY?.trim();

  if (supabaseAnonKey) {
    headers.apikey = supabaseAnonKey;
    if (isJwtLike(supabaseAnonKey)) {
      headers.Authorization = `Bearer ${supabaseAnonKey}`;
    }
  }

  return headers;
}

async function parseJson<T>(response: Response): Promise<T | null> {
  try {
    return (await response.json()) as T;
  } catch {
    return null;
  }
}

function getApiErrorMessage(payload: ApiErrorResponse | null): string | null {
  if (typeof payload?.details === 'string' && payload.details.trim().length > 0) {
    return payload.details;
  }

  if (
    payload?.error &&
    typeof payload.error === 'object' &&
    typeof payload.error.message === 'string' &&
    payload.error.message.trim().length > 0
  ) {
    return payload.error.message;
  }

  if (typeof payload?.message === 'string' && payload.message.trim().length > 0) {
    return payload.message;
  }

  if (typeof payload?.error === 'string' && payload.error.trim().length > 0) {
    return payload.error;
  }

  return null;
}

function getSessionExpiration(token: string, expiresIn?: number): number {
  if (typeof expiresIn === 'number' && Number.isFinite(expiresIn) && expiresIn > 0) {
    return Date.now() + expiresIn * 1000;
  }

  const jwtExpiration = getJwtExpiration(token);

  if (jwtExpiration && jwtExpiration > Date.now()) {
    return jwtExpiration;
  }

  return Date.now() + FALLBACK_SESSION_SECONDS * 1000;
}

function getRefreshExpiration(expiresIn?: number, previousExpiresAt?: number): number | undefined {
  if (typeof expiresIn === 'number' && Number.isFinite(expiresIn) && expiresIn > 0) {
    return Date.now() + expiresIn * 1000;
  }

  if (
    typeof previousExpiresAt === 'number' &&
    Number.isFinite(previousExpiresAt) &&
    previousExpiresAt > Date.now()
  ) {
    return previousExpiresAt;
  }

  return undefined;
}

function createSession(input: {
  token: string;
  business: BusinessSessionData;
  expiresIn?: number;
  refreshToken?: string;
  refreshExpiresIn?: number;
  previousSession?: AuthSession;
}): AuthSession {
  const refreshToken = input.refreshToken ?? input.previousSession?.refreshToken;
  const refreshExpiresAt = refreshToken
    ? getRefreshExpiration(input.refreshExpiresIn, input.previousSession?.refreshExpiresAt)
    : undefined;

  return {
    token: input.token,
    refreshToken,
    business: input.business,
    expiresAt: getSessionExpiration(input.token, input.expiresIn),
    refreshExpiresAt,
  };
}

function shouldRefresh(session: AuthSession, force = false): boolean {
  return force || session.expiresAt <= Date.now() + REFRESH_BEFORE_EXPIRY_MS;
}

async function postLogout(refreshToken: string): Promise<void> {
  try {
    await fetch(buildApiUrl('/auth/logout'), {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify({ refreshToken }),
    });
  } catch {
    // Local logout should not depend on network availability.
  }
}

async function requestOtpAt(
  phone: string,
  path: string
): Promise<{ message: string; challengeId?: string }> {
  try {
    const response = await fetch(buildApiUrl(path), {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify({ phone }),
    });

    const payload = await parseJson<RequestOtpResponse & ApiErrorResponse>(response);

    if (!response.ok || payload?.ok === false || payload?.success === false) {
      const message =
        getApiErrorMessage(payload) ??
        (response.status >= 500
          ? 'No fue posible enviar el codigo en este momento.'
          : 'No pudimos procesar la solicitud. Revisa el numero e intenta de nuevo.');
      throw new AuthServiceError(message, response.status);
    }

    return {
      message:
        typeof payload?.message === 'string' && payload.message.trim().length > 0
          ? payload.message
          : DEFAULT_REQUEST_OTP_MESSAGE,
      challengeId: typeof payload?.challengeId === 'string' ? payload.challengeId : undefined,
    };
  } catch (error) {
    if (error instanceof AuthServiceError) {
      throw error;
    }

    throw new AuthServiceError(DEFAULT_NETWORK_ERROR_MESSAGE);
  }
}

export function requestOtp(phone: string): Promise<{ message: string; challengeId?: string }> {
  return requestOtpAt(phone, '/request-otp');
}

export function requestRegistrationOtp(
  phone: string
): Promise<{ message: string; challengeId?: string }> {
  return requestOtpAt(phone, '/registration/request-otp');
}

export async function verifyRegistrationOtp(input: VerifyOtpRequest): Promise<string> {
  try {
    const response = await fetch(buildApiUrl('/registration/verify-otp'), {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify(input),
    });
    const payload = await parseJson<
      ApiErrorResponse & { ok?: boolean; verificationToken?: string }
    >(response);

    if (!response.ok || payload?.ok !== true || !payload.verificationToken) {
      throw new AuthServiceError(
        getApiErrorMessage(payload) ?? DEFAULT_VERIFY_ERROR_MESSAGE,
        response.status
      );
    }

    return payload.verificationToken;
  } catch (error) {
    if (error instanceof AuthServiceError) throw error;
    throw new AuthServiceError(DEFAULT_NETWORK_ERROR_MESSAGE);
  }
}

export async function verifyOtp({
  challengeId,
  phone,
  otp,
}: VerifyOtpRequest): Promise<AuthSession> {
  try {
    const body: VerifyOtpRequest = { phone, otp };

    if (challengeId) {
      body.challengeId = challengeId;
    }

    const response = await fetch(buildApiUrl('/auth/verify-otp'), {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify(body),
    });

    const payload = await parseJson<VerifyOtpResponse & ApiErrorResponse>(response);

    const isSuccess = payload?.ok === true || payload?.success === true;
    const token = payload?.accessToken ?? payload?.token;
    const expiresIn = payload?.expiresInSeconds ?? payload?.expiresIn;
    const refreshExpiresIn = payload?.refreshExpiresInSeconds ?? payload?.refreshExpiresIn;

    if (!response.ok || !isSuccess || !token || !payload?.business) {
      const message =
        getApiErrorMessage(payload) ??
        (response.status >= 500
          ? 'No fue posible validar el codigo en este momento.'
          : DEFAULT_VERIFY_ERROR_MESSAGE);
      throw new AuthServiceError(message, response.status);
    }

    const session = createSession({
      token,
      refreshToken: payload.refreshToken,
      business: payload.business,
      expiresIn,
      refreshExpiresIn,
    });

    authStorage.setSession(session);
    return session;
  } catch (error) {
    if (error instanceof AuthServiceError) {
      throw error;
    }

    throw new AuthServiceError(DEFAULT_NETWORK_ERROR_MESSAGE);
  }
}

async function refreshStoredSession(session: AuthSession): Promise<AuthSession | null> {
  if (!session.refreshToken) {
    if (session.expiresAt <= Date.now()) {
      authStorage.clearSession();
      return null;
    }

    return session;
  }

  try {
    const response = await fetch(buildApiUrl('/auth/refresh'), {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify({ refreshToken: session.refreshToken }),
    });

    const payload = await parseJson<RefreshSessionResponse & ApiErrorResponse>(response);

    const isSuccess = payload?.ok === true || payload?.success === true;
    const token = payload?.accessToken ?? payload?.token;
    const expiresIn = payload?.expiresInSeconds ?? payload?.expiresIn;
    const refreshExpiresIn = payload?.refreshExpiresInSeconds ?? payload?.refreshExpiresIn;

    if (!response.ok || !isSuccess || !token || !payload?.business || !payload.refreshToken) {
      if (response.status === 401 || response.status === 403 || session.expiresAt <= Date.now()) {
        authStorage.clearSession();
        return null;
      }

      return session;
    }

    const refreshedSession = createSession({
      token,
      refreshToken: payload.refreshToken,
      business: payload.business,
      expiresIn,
      refreshExpiresIn,
      previousSession: session,
    });

    authStorage.setSession(refreshedSession);
    return refreshedSession;
  } catch {
    if (session.expiresAt > Date.now()) {
      return session;
    }

    authStorage.clearSession();
    return null;
  }
}

export async function refreshSession(options?: { force?: boolean }): Promise<AuthSession | null> {
  const session = authStorage.getSession();
  if (!session) {
    return null;
  }

  if (!shouldRefresh(session, options?.force)) {
    return session;
  }

  if (!activeRefreshPromise) {
    activeRefreshPromise = refreshStoredSession(session).finally(() => {
      activeRefreshPromise = null;
    });
  }

  return activeRefreshPromise;
}

export async function getValidSession(): Promise<AuthSession | null> {
  return refreshSession();
}

export function logout(): void {
  const refreshToken = authStorage.getSession()?.refreshToken;
  authStorage.clearSession();

  if (refreshToken) {
    void postLogout(refreshToken);
  }
}

export function getSession(): AuthSession | null {
  return authStorage.getSession();
}

export function isAuthenticated(): boolean {
  return authStorage.isAuthenticated();
}

export { AuthServiceError };
